import { isNil } from 'lodash';
import { isArray, isEmpty, isFunction, get } from 'lodash';
import { environment as env } from '../../assets/js/environments';

import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';


export enum MODAL_WIDTH {
  XL = 'min(1152px, 86%)',
  LG = 'min(768px, 86%)',
  MD = 'min(525px, 86%)',
  SM = 'min(425px, 86%)',
}

export enum MODAL_TYPE {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  ADD = 'ADD',
}

export enum MODE {
  EDIT = 'EDIT',
  CREATE = 'CREATE',
}

export enum INTEGRATIONS {
  ACTIMO = 'actimo',
  BRANDWATCH = 'brandwatch',
}

export enum PLATFORMS {
  SOCIUUHUB = 'sociuuhub',
  ADMIN = 'admin',
}

export enum FEATURES {
  SSO = 'sso',
  OPT_IN = 'opt_in',
  AUTO_FLOW = 'smartflow',
  SELF_SIGNUP = 'self_signup',
  COOKIE_CONSENT = 'cookie_consent',
  SOCIAL_CONNECT = 'social_connect',
  USER_GROUP_COUNTRY_ASSOCIATION = 'usergroup_country_association',
  ENTRA = 'entra',
}

export type ModalType = typeof MODAL_TYPE;

export const MAX_NUMBER_OF_ROWS: number = 10001;
export const SENDOUT_SETTINGS_TEXT: string = 'Calculating last Send-out ...';
export const IMAGE_MIME_TYPE: string[] = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'];
export const IMAGE_MIME_TYPE_ACCEPT: string = IMAGE_MIME_TYPE.join(', ');
export const VIDEO_MIME_TYPE: string[] = ['video/mp4'];
export const VIDEO_MIME_TYPE_ACCEPT: string = VIDEO_MIME_TYPE.join(', ');
export const FILE_MIME_TYPE: string[] = [
  // 'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/pdf',
  // 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
export const FILE_MIME_TYPE_ACCEPT: string = FILE_MIME_TYPE.join(', ');

export enum FILE_UPLOAD_STATE {
  PRE_UPLOAD = 'pre-upload',
  IN_PROGRESS = 'in-progress',
  SUCCESS = 'success',
  ABORTED = 'aborted',
  ERROR = 'error',
}

export enum TYPE_ID {
  PHOTO = 100,
  VIDEO = 200,
  TEXT = 300,
  DOCUMENT = 400,
  JOB = 500,
  LINK = 600,
  OTHER = 700,
}

export enum DOCUMENT_TYPE {
  // doc = 'application/msword',
  docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // ppt = 'application/vnd.ms-powerpoint',
  pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pdf = 'application/pdf',
}

export enum IMAGE_TYPE {
  png = 'image/png',
  jpg = 'image/jpg',
  gif = 'image/gif',
  jpeg = 'image/jpeg',
}

export enum IMAGE_TYPE_EXTENSIONS {
  'image/png' = '.png',
  'image/jpg' = '.jpeg',
  'image/gif' = '.gif',
  'image/jpeg' = '.jpeg',
}

export enum VIDEO_TYPE {
  mp4 = 'video/mp4',
}

export enum VIDEO_TYPE_EXTENSIONS {
  'video/mp4' = '.mp4',
}

export interface IStateProperties {
  icon?: string;
  class?: string;
  color?: string;
  fileName?: string;
}

export interface IPaginationServer {
  to: number;
  from: number;
  total: number;
  per_page: number;
  last_page: number;
  current_page: number;
}

export interface IPagination {
  to: number;
  from: number;
  total: number;
  perPage: number;
  lastPage: number;
  currentPage: number;
}

export const FILE_STATE_ICON_MAPPING: Record<FILE_UPLOAD_STATE, IStateProperties> = {
  [FILE_UPLOAD_STATE.PRE_UPLOAD]: {
    icon: 'mat_outline:cloud',
    color: '#A4ABB6',
    class: 'pre-upload bg-primary-50',
    fileName: 'cloud',
  },
  [FILE_UPLOAD_STATE.IN_PROGRESS]: {
    icon: 'mat_outline:cloud_upload',
    color: '#C9E1EF',
    class: 'in-progress',
    fileName: 'inProgress',
  },
  [FILE_UPLOAD_STATE.SUCCESS]: { icon: 'mat_outline:cloud_done', color: '#5B751D', class: 'success', fileName: 'success' },
  [FILE_UPLOAD_STATE.ABORTED]: { icon: '', color: '#FFB302', class: 'abort', fileName: 'abort' },
  [FILE_UPLOAD_STATE.ERROR]: { icon: 'mat_outline:error_outline', color: '#FF3838', class: 'error', fileName: 'error' },
};

export const isEmptyArray: (array: any) => boolean = (array: any): boolean => {
  return isNil(array) || (isArray(array) && isEmpty(array));
};

export let environment: any = env;
export const DATE_FORMAT: string = 'YYYY-MM-DD';
export const DATE_FORMAT_FOR_PIPE: string = 'yyyy-MM-dd';
export const DATE_FORMAT_WITHOUT_SECONDS: string = 'YYYY-MM-DD HH:mm';
export const DATE_FORMAT_WITH_SECONDS: string = 'YYYY-MM-DD HH:mm:ss';

export function toParagraphCase(text: string): string {
  // Regular expression to match sentence enders (., !, ?) followed by a space or end of string
  const sentenceEndersRegEx = /([.!?]\s*|\s+|^)/g;

  let result = text.toLowerCase().replace(sentenceEndersRegEx, (match) => {
    // Trim the match to handle cases where multiple spaces follow a sentence ender
    // and ensure only the first character of the next sentence is capitalized.
    return match.toUpperCase();
  });

  // Ensure the very first character of the text is capitalized
  if (result.length > 0) {
    result = result.charAt(0).toUpperCase() + result.slice(1);
  }

  return result;
}

export const SUPPORTED_LANGUAGES: { id: string; label: string }[] = [
  {
    id: 'en-US',
    label: 'English USA',
  },
  {
    id: 'tr-TR',
    label: 'Turkish',
  },
  {
    id: 'nl-NL',
    label: 'Dutch',
  },
  {
    id: 'de-DE',
    label: 'German',
  },
  {
    id: 'da-DK',
    label: 'Danish',
  },
  {
    id: 'en-UK',
    label: 'English',
  },
  {
    id: 'es-ES',
    label: 'Spanish',
  },
  {
    id: 'fr-FR',
    label: 'French',
  },
  {
    id: 'fi-FI',
    label: 'Finnish',
  },
  {
    id: 'nb-NO',
    label: 'Norwegian',
  },
  {
    id: 'sv-SE',
    label: 'Swedish',
  },
];

export function initQueryParams(router: Router): any {
  const state: RouterStateSnapshot = router.routerState.snapshot;
  const tree: UrlTree = router.parseUrl(state.url);
  delete tree.queryParams['magic-token'];
  const returnUrl: string = tree.toString();
  return state.url === '/dashboard' ? {} : { queryParams: { returnUrl: returnUrl } };
}

export function dataURLtoFile(dataurl: string, filename: string) {
  try {
    // Validate input
    if (!dataurl || typeof dataurl !== 'string' || !dataurl.includes(',')) {
      throw new Error('Invalid data URL format');
    }

    var arr = dataurl.split(',');
    if (arr.length < 2) {
      throw new Error('Invalid data URL structure');
    }

    var mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error('Invalid MIME type in data URL');
    }

    var mime = mimeMatch[1];
    var base64Data = arr[arr.length - 1];

    // Validate base64 data before decoding
    if (!base64Data || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64Data)) {
      throw new Error('Invalid base64 data');
    }

    var bstr = atob(base64Data);
    var n = bstr.length;
    var u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Error converting data URL to file:', error);
    // Return a minimal valid file as fallback
    return new File([''], filename, { type: 'application/octet-stream' });
  }
}

export function getDomainFromUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname; // Returns the domain
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export const LINKEDIN_CONNECTION_EXPIRED_MESSAGE = 'Your LinkedIn connection has expired. Reconnect LinkedIn to continue.';

export function isLinkedInConnectionExpiredError(err: any): boolean {
  const error = err?.error ?? err;

  return error?.error_type === 'InvalidAccessTokenException' ||
    error?.code === 'EXPIRED_ACCESS_TOKEN' ||
    error?.serviceErrorCode === 65602;
}

export function getValidationErrorMessage(err: any): string {
  if (typeof err === 'string') return err;

  // Handle specific error codes with user-friendly messages
  if (isLinkedInConnectionExpiredError(err)) {
    return LINKEDIN_CONNECTION_EXPIRED_MESSAGE;
  }

  let dataMessage: string = '';
  if (err?.data) {
    const key: string = Object.keys(err.data)?.[0];
    dataMessage = isArray(err.data[key]) ? err.data[key][0] : err.data[key];
  }
  const message: string = dataMessage || err?.message || err?.messages?.[0] || ssoSessionReason(err) || 'Something went wrong, please try again.';
  return message;
}

/**
 * The reason in Apex's SSO session envelope, `{ success, status, data, error }` (SSOController::
 * getSessionStatus), and nothing else: a body that merely carries a string `error` — an OAuth
 * `{ error: 'invalid_grant' }`, say — keeps the generic sentence. Read here rather than in
 * OAuthService because the interceptor reduces a 400 body through this helper before any service
 * sees it, so the service alone could not recover the reason.
 */
const ssoSessionReason = (err: any): string => {
  const isEnvelope = err !== null && typeof err === 'object' && 'success' in err && typeof err.status === 'string';
  return isEnvelope && typeof err.error === 'string' ? err.error : '';
};

/**
 * Normalises an API `messages` payload into a single displayable string.
 * Endpoints are inconsistent: some return a plain string, others an array of strings.
 * Indexing a string with `[0]` yields only its first character, so always normalise
 * before handing the value to the notification service.
 */
export function getResponseMessage(messages: string | string[], fallback: string = ''): string {
  const message: string = isArray(messages) ? messages[0] : messages;
  return message || fallback;
}

export function convertBase64ToBlob(base64: string, type: string): Blob {
  const byteString = atob(base64);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const int8Array = new Uint8Array(arrayBuffer);
  for (let i = 0; i < byteString.length; i++) {
    int8Array[i] = byteString.charCodeAt(i);
  }
  return new Blob([int8Array], { type: type });
}

export function convertBlobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type });
}

export function canUseToString(value) {
  return isFunction(get(value, 'toString'));
}

export function transformPagination(meta: IPaginationServer): IPagination {
  return {
    to: meta.to,
    from: meta.from,
    total: meta.total,
    perPage: meta.per_page,
    lastPage: meta.last_page,
    currentPage: meta.current_page,
  };
}

export function convertToDays(unit: 'days' | 'weeks' | 'months', value: number): number {
  switch (unit) {
    case 'days':
      return value;
    case 'weeks':
      return value * 7;
    case 'months':
      return value * 30; // Approximate: 1 month = 30 days
    default:
      throw new Error(`Invalid unit: ${unit}`);
  }
}
